import { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "expo-router";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { authApi, mapAuthError } from "@/lib/api/auth";
import { getPostLoginRedirectPath } from "@/lib/authRedirect";
import { otpStore } from "@/lib/otpStore";
import { tempPasswordStore } from "@/lib/tempPasswordStore";
import { loginPasswordSchema, type LoginPasswordValues } from "../schemas/authSchemas";
import type { AuthUser as ContextAuthUser } from "@/context/AuthContext";
import type { AuthUser as ApiAuthUser } from "@chinese-vendor/schemas";

export type LoginMode = "password" | "otp";

function toContextUser(apiUser: ApiAuthUser): ContextAuthUser {
  return {
    id: apiUser.id,
    email: apiUser.email,
    firstName: apiUser.first_name,
    lastName: apiUser.last_name,
    roles: apiUser.roles,
    vendorStatus: apiUser.vendor_status,
    applicationId: apiUser.application_id ?? null,
    temporaryPasswordSet: apiUser.temporary_password_set ?? false,
  };
}

export function useLoginForm() {
  const { login } = useAuth();
  const router = useRouter();
  const [mode, setMode] = useState<LoginMode>("password");

  const passwordForm = useForm<LoginPasswordValues>({
    resolver: zodResolver(loginPasswordSchema),
    defaultValues: { email: "", password: "" },
  });

  const sendOTPMutation = useMutation({
    mutationFn: (email: string) => authApi.requestOTP({ email }),
    onSuccess: (data, email) => {
      otpStore.set(data.otp_token);
      router.push({
        pathname: "/(auth)/otp-verification" as never,
        params: { email },
      });
    },
  });

  const passwordLoginMutation = useMutation({
    mutationFn: (values: LoginPasswordValues) => authApi.loginWithPassword(values),
    onSuccess: async (result, variables) => {
      if (result.user.temporary_password_set) {
        tempPasswordStore.set(variables.password);
      }
      await login(result.token, toContextUser(result.user));
      router.replace(
        getPostLoginRedirectPath(
          result.user.roles,
          result.user.vendor_status,
          result.user.temporary_password_set
        )
      );
    },
  });

  const switchMode = useCallback(
    (next: LoginMode) => {
      setMode(next);
      sendOTPMutation.reset();
      passwordLoginMutation.reset();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleSendOTP = useCallback(async () => {
    const isValid = await passwordForm.trigger("email");
    if (!isValid) return;
    const email = passwordForm.getValues("email");
    await sendOTPMutation.mutateAsync(email).catch(() => {});
  }, [passwordForm, sendOTPMutation.mutateAsync]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePasswordLogin = passwordForm.handleSubmit(async (values) => {
    await passwordLoginMutation.mutateAsync(values).catch(() => {});
  });

  const rawError = sendOTPMutation.error ?? passwordLoginMutation.error;
  const error = rawError ? mapAuthError(rawError) : null;

  const isLoading = sendOTPMutation.isPending || passwordLoginMutation.isPending;

  return {
    mode,
    switchMode,
    passwordForm,
    otpSent: sendOTPMutation.isSuccess,
    isLoading,
    error,
    handleSendOTP,
    handlePasswordLogin,
  };
}
